<template>
	<div class="d-flex flex-column w-100">
		<div class="d-flex flex-row align-baseline ga-1 pa-0 justify-space-between flex-wrap">
			<div class="d-flex align-baseline ga-2">
				<div class="header font-weight-bold" data-test-id="timeline-heading">{{ heading }}:</div>
				<div class="result-count">({{ displayedMediaCount }}{{ mediaCountCapped ? "+" : "" }})</div>
				<!-- LSP-16659: "· Showing documents" / "· Filtered", derived by the parent from the APPLIED request
				     (never the live Filters UI). Hidden in select mode, where the header is the bulk-action bar. -->
				<div v-if="headingSuffix && !selecting" v-test="'timeline-heading-suffix'" class="result-count">{{ headingSuffix }}</div>
				<div v-if="pendingMediaCount > 0" v-test="'timeline-pending-count'" class="result-count">
					- {{ pendingMediaCount }} Pending
				</div>
			</div>
			<div v-if="mediaItems.length > 0" class="d-flex flex justify-start ga-2 align-center flex-wrap">
				<v-switch
					v-if="parentType === 'project'"
					v-model="dayView"
					label="Day view"
					inset
					hide-details
					density="compact"
					:disabled="dayViewDisabled"
					class="day-view-checkbox" />
				<v-btn
					v-if="selecting && hasPotentialSelectableMediaItems && !allSelectableMediaItemsSelected"
					v-test="'timeline-add-to-select-all'"
					v-track="'timeline-add-to-select-all-button'"
					color="grey-lighten-4"
					elevation="1"
					size="small"
					rounded="pill"
					:loading="selectingAll"
					@click="selectAll"
					>Select All</v-btn
				>
				<v-btn
					v-if="selecting && allSelectableMediaItemsSelected"
					v-test="'timeline-add-to-deselect-all'"
					v-track="'timeline-add-to-deselect-all-button'"
					color="grey-lighten-4"
					elevation="1"
					rounded="pill"
					size="small"
					@click="deselectAll"
					>Deselect All</v-btn
				>
				<v-btn
					v-if="!selecting && hasPotentialSelectableMediaItems"
					rounded="pill"
					color="grey-lighten-4"
					size="small"
					elevation="1"
					@click="toggleSelecting"
					>Select</v-btn
				>
				<v-btn
					v-if="selecting"
					v-test="'timeline-selection-cancel'"
					color="grey-lighten-4"
					elevation="1"
					rounded="pill"
					size="small"
					@click="toggleSelecting"
					>Cancel</v-btn
				>
				<v-btn
					v-if="parentType === 'project' && selecting && selectingChecklistMedia"
					v-test="'timeline-add-to-checklist'"
					v-track="'timeline-add-to-checklist-button'"
					color="primary"
					elevation="1"
					rounded="pill"
					size="small"
					:disabled="selectedMediaItems.length === 0"
					@click="doneSelecting"
					>Add Selected Content to Checklist</v-btn
				>

				<div class="d-flex flex-wrap ga-1 align-center">
					<!-- LSP-19565: run a Sparky report scoped to only the selected media. The parent already
					     derives selectedFileIds from selectedMediaItems, so this only needs to start the flow. -->
					<v-btn
						v-if="showActionButtons && hasScribeFeature && selectionHasSparkyAnalyzableMedia"
						v-test="'timeline-run-sparky'"
						v-track="'timeline-run-sparky-button'"
						color="primary"
						elevation="1"
						rounded="pill"
						size="small"
						@click="$emit('runSparkyReport')">
						<SeanIcon :height="18" :width="18" :enable-tooltip="false" class="mr-1" />
						Run a Spark on {{ selectedMediaItems.length }} selected
					</v-btn>
					<v-btn
						v-if="showActionButtons"
						v-test="'timeline-bulk-tag'"
						v-track="'timeline-bulk-tag-button'"
						variant="plain"
						max-height="28px"
						icon="icon-shop-tag-stroke"
						@click="bulkTagDialogVisible = true">
					</v-btn>

					<v-tooltip v-if="showActionButtons && parentType === 'project'" location="top">
						<template #activator="{ props: sheetProps }">
							<v-btn
								v-bind="sheetProps"
								v-test="'timeline-create-sheet'"
								v-track="'timeline-create-sheet-button'"
								variant="plain"
								max-height="28px"
								icon="icon-form"
								@click="$emit('createSheet')">
							</v-btn>
						</template>
						<span>Create sheet with selected media</span>
					</v-tooltip>

					<v-tooltip
						v-if="selectedMediaItems.length > 0"
						top
						:open-on-focus="false"
						:disabled="downloading || selectedMediaItems.length <= 50">
						<template #activator="{ props }">
							<div v-bind="props">
								<v-btn
									v-if="!selectingChecklistMedia"
									v-test="'timeline-bulk-download'"
									variant="plain"
									:loading="downloading"
									icon="icon-download"
									max-height="28px"
									aria-label="Download selected media"
									:disabled="disableDeleteButton || (!display.mobile.value && selectedMediaItems.length > 50)"
									@click="downloadMediaItems">
								</v-btn>
							</div>
						</template>
						<span> You can only download up to 50 items at a time. </span>
					</v-tooltip>

					<v-btn
						v-if="showActionButtons"
						v-test="'timeline-delete'"
						v-track="'timeline-delete-button'"
						:disabled="disableDeleteButton"
						variant="plain"
						icon="icon-trash-full-stroke"
						max-height="28px"
						color="error"
						@click="deleteMediaItems">
					</v-btn>
				</div>
			</div>
		</div>
		<div class="d-flex" :class="dateLabelClass">{{ dateLabel }}</div>

		<!-- Day View Mode -->
		<div v-if="dayView && groupedMediaByDate.length > 0" id="media-grid-container" ref="mediaGridContainer" style="overflow-y: auto">
			<div
				v-for="group in groupedMediaByDate"
				:key="group.dateKey"
				class="day-group mb-4"
				:class="{ 'drag-over': dragOverDateKey === group.dateKey }"
				@dragover="onDragOver($event, group.dateKey)"
				@dragleave="onDragLeave"
				@drop="onDrop($event, group.dateKey)">
				<div class="d-flex align-center justify-space-between pa-2">
					<div class="font-weight-bold text-body-1">{{ group.dateLabel }}</div>
					<v-btn
						v-if="!isViewOnly && !hasFreeTrialEnded && isAuthenticated"
						color="primary"
						variant="flat"
						rounded="xl"
						size="small"
						prepend-icon="mdi-download"
						:loading="downloadingDay === group.dateKey"
						:disabled="downloading"
						@click="downloadDay(group)">
						{{ group.dateKey === UNDATED_DATE_KEY ? "Download" : "Download Day" }}
					</v-btn>
				</div>
				<div class="media-grid">
					<!-- Skeleton placeholders only in today's day group -->
					<template v-if="showPendingPlaceholdersInGroup(group.dateKey)">
						<div v-for="index in pendingImages" :key="`pending-image-${index}`" class="media-grid-item">
							<v-skeleton-loader type="image" height="100%" width="100%" />
						</div>
						<div v-for="index in pendingVideos" :key="`pending-video-${index}`" class="media-grid-item">
							<v-skeleton-loader type="image" height="100%" width="100%" />
						</div>
						<div v-for="index in serverPendingVideoPlaceholders" :key="`pending-server-video-${index}`" class="media-grid-item">
							<v-skeleton-loader type="image" height="100%" width="100%" />
						</div>
						<div
							v-for="index in pendingDocs"
							:key="`pending-document-${index}`"
							v-test="'timeline-pending-document'"
							class="media-grid-item">
							<v-skeleton-loader type="image" height="100%" width="100%" />
						</div>
					</template>
					<div
						v-for="mediaItem in group.items"
						:key="resultIdentity(mediaItem)"
						class="media-grid-item"
						:data-date="mediaItem.created"
						:data-id="resultIdentity(mediaItem)"
						:draggable="canEditArtifactDates && !selecting && !!mediaItem.created && isDateEditableEntity(mediaItem)"
						@dragstart="onDragStart($event, mediaItem)"
						@dragend="onDragEnd"
						@click="handleMediaClick(mediaItem)">
						<v-chip v-if="mediaItem.status === 'pending' && isDebugMode" size="x-small" color="warning" class="pending-badge"
							>PENDING</v-chip
						>
						<div v-if="mediaItem.entityType === 'snapshot'" class="preview-container h-100">
							<v-img
								:lazy-src="getImageUrl(mediaItem)"
								:src="getImageUrl(mediaItem)"
								:alt="mediaItem.name"
								height="100%"
								cover
								responsive>
								<img
									:src="getImageUrl(mediaItem)"
									:alt="mediaItem.name"
									class="media-copy-overlay"
									@contextmenu.stop
									@click.stop="handleMediaClick(mediaItem)" />
							</v-img>
							<a
								v-if="showSessionNumber"
								:href="sessionRoute(mediaItem)"
								class="session-number px-1"
								:aria-label="`Open session ${mediaItem.number}`"
								@click="navigateToSession(mediaItem, $event)">
								Session #{{ mediaItem.number }}
							</a>
						</div>
						<VideoPreview
							v-if="mediaItem.entityType === 'recording'"
							:parent-type="parentType"
							:session-number="mediaItem.number"
							:session-id="mediaItem.conversationId"
							:thumbnail="mediaItem.thumbnailUrl"
							:incomplete-upload="isIncompleteRecordingUpload(mediaItem)"
							@open-incomplete-recording="onItemClick(resultIdentity(mediaItem))"
							:media-organization-id="mediaItem.organizationId"
							:me="me" />
						<DocumentTile v-if="mediaItem.entityType === 'document'" :document="mediaItem" :selecting="selecting">
							<template #session-label>
								<a
									v-if="showSessionNumber"
									:href="sessionRoute(mediaItem)"
									class="session-number session-number--document px-1"
									:aria-label="`Open session ${mediaItem.number}`"
									@click="navigateToSession(mediaItem, $event)">
									Session #{{ mediaItem.number }}
								</a>
							</template>
						</DocumentTile>

						<div class="media-tile-actions d-flex flex-column align-end ga-1">
							<div
								v-if="selecting && isItemSelectable(mediaItem)"
								class="selection-bubble"
								:class="{ selected: isItemSelected(mediaItem) }"
								@click.stop="toggleItem(mediaItem)">
								<span v-if="isItemSelected(mediaItem)" class="text">{{ getItemSelectionIndex(mediaItem) }}</span>
							</div>
						</div>
						<v-tooltip v-if="selecting && !isItemSelectable(mediaItem)" activator="parent" location="top" :open-on-focus="true">
							{{ unselectableReason(mediaItem) }}
						</v-tooltip>
					</div>
				</div>
			</div>
			<div v-if="mediaItems.length" ref="paginationSentinel" class="timeline-pagination-sentinel" aria-hidden="true"></div>
		</div>

		<!-- Normal (flat) View Mode -->
		<div v-else id="media-grid-container" ref="mediaGridContainer" class="media-grid" style="overflow-y: auto">
			<!-- LSP-15573: an admin video upload that started and never finished. Rendered only on an
			     otherwise empty timeline (see showFailedVideoUploadState) so a successful re-upload takes it
			     away, and in place of the "There's no media here yet" label rather than beside it. -->
			<div
				v-if="showFailedVideoUploadState"
				class="failed-upload-placeholder d-flex flex-column align-center justify-center text-center pa-3"
				data-test-id="timeline-failed-upload-placeholder"
				role="status"
				aria-live="polite"
				aria-atomic="true">
				<!-- LSP-15573 follow-up: a WARNING, not an error. The session is intact and the action is
				     retryable, so the error-red circle-! overstated it. `$warning` resolves through the
				     iconset aliases to icon-warning-triangle — the same glyph v-alert type="warning" uses,
				     so this tile now speaks the app's existing warning language rather than a second one.
				     Note mdi-* names must be registered in src/iconsets/mdi.ts, which is why this is the
				     alias and not mdi-alert-outline. -->
				<v-icon icon="$warning" color="warning" size="32" />
				<div class="text-body-2 font-weight-medium mt-2">{{ failedVideoUploadMessage }}</div>
				<div class="text-caption text-medium-emphasis mt-1">{{ failedVideoUploadDetail }}</div>
			</div>
			<!-- Skeleton placeholders for pending uploads. LSP-16659: documents live in this grid now, so an
			     uploading document gets a skeleton here too; the parent retires it once the row arrives. -->
			<div v-for="index in pendingImages" :key="`pending-image-${index}`" class="media-grid-item">
				<v-skeleton-loader type="image" height="100%" width="100%" />
			</div>
			<div v-for="index in pendingVideos" :key="`pending-video-${index}`" class="media-grid-item">
				<v-skeleton-loader type="image" height="100%" width="100%" />
			</div>
			<div v-for="index in serverPendingVideoPlaceholders" :key="`pending-server-video-${index}`" class="media-grid-item">
				<v-skeleton-loader type="image" height="100%" width="100%" />
			</div>
			<div
				v-for="index in pendingDocs"
				:key="`pending-document-${index}`"
				v-test="'timeline-pending-document'"
				class="media-grid-item">
				<v-skeleton-loader type="image" height="100%" width="100%" />
			</div>
			<div
				v-for="mediaItem in mediaItems"
				:key="resultIdentity(mediaItem)"
				class="media-grid-item"
				:data-date="mediaItem.created"
				:data-id="resultIdentity(mediaItem)"
				@click="handleMediaClick(mediaItem)">
				<div v-if="mediaItem.entityType === 'snapshot'" class="preview-container h-100">
					<v-img
						:lazy-src="getImageUrl(mediaItem)"
						:src="getImageUrl(mediaItem)"
						:alt="mediaItem.name"
						height="100%"
						cover
						responsive>
						<!-- Invisible img overlay for right-click copy functionality -->
						<img
							:src="getImageUrl(mediaItem)"
							:alt="mediaItem.name"
							class="media-copy-overlay"
							@contextmenu.stop
							@click.stop="handleMediaClick(mediaItem)" />
					</v-img>
					<a
						v-if="showSessionNumber"
						:href="sessionRoute(mediaItem)"
						class="session-number px-1"
						:aria-label="`Open session ${mediaItem.number}`"
						@click="navigateToSession(mediaItem, $event)">
						Session #{{ mediaItem.number }}
					</a>
				</div>
				<VideoPreview
					v-if="mediaItem.entityType === 'recording'"
					:parent-type="parentType"
					:session-number="mediaItem.number"
					:session-id="mediaItem.conversationId"
					:thumbnail="mediaItem.thumbnailUrl"
					:incomplete-upload="isIncompleteRecordingUpload(mediaItem)"
					@open-incomplete-recording="onItemClick(resultIdentity(mediaItem))"
					:media-organization-id="mediaItem.organizationId"
					:me="me" />
				<DocumentTile v-if="mediaItem.entityType === 'document'" :document="mediaItem" :selecting="selecting">
					<template #session-label>
						<a
							v-if="showSessionNumber"
							:href="sessionRoute(mediaItem)"
							class="session-number session-number--document px-1"
							:aria-label="`Open session ${mediaItem.number}`"
							@click="navigateToSession(mediaItem, $event)">
							Session #{{ mediaItem.number }}
						</a>
					</template>
				</DocumentTile>

				<div class="media-tile-actions d-flex flex-column align-end ga-1">
					<div
						v-if="selecting && isItemSelectable(mediaItem)"
						class="selection-bubble"
						:class="{ selected: isItemSelected(mediaItem) }"
						@click.stop="toggleItem(mediaItem)">
						<span v-if="isItemSelected(mediaItem)" class="text">{{ getItemSelectionIndex(mediaItem) }}</span>
					</div>
				</div>
				<v-tooltip v-if="selecting && !isItemSelectable(mediaItem)" activator="parent" location="top" :open-on-focus="true">
					{{ unselectableReason(mediaItem) }}
				</v-tooltip>
			</div>
			<div v-if="mediaItems.length" ref="paginationSentinel" class="timeline-pagination-sentinel" aria-hidden="true"></div>
		</div>
		<div v-if="loading" class="text-center mt-2">
			<v-progress-circular indeterminate color="primary" width="2"></v-progress-circular>
		</div>

		<TagSelectionDialog
			:dialog-visible="bulkTagDialogVisible"
			:is-bulk="true"
			:pre-selected-items="[]"
			@cancel="bulkTagDialogVisible = false"
			@agree="applyBulkTags" />
	</div>
</template>

<script lang="ts" setup>
	import { IndexableDocument } from "@/types/search/IndexableDocument";
	import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, PropType, ref, watch } from "vue";
	import moment from "moment-timezone";
	import { SelectedMediaItem } from "@/types/media/SelectedMediaItem";
	import { ArtifactType } from "@/enums/ArtifactType";
	import { downloadMediaFiles, downloadMediaFilesByDate } from "@/helpers/download";
	import { groupMediaByDate, UNDATED_DATE_KEY, type DayGroup } from "@/helpers/dayView";
	import { getDateTokens } from "@/helpers/dateFormat";
	import { loadAllPages } from "@/components/search/utils/pagedSelection";
	import { couldNot, listEmptyMessage } from "@/helpers/userMessages";
	import timezones from "@/helpers/timezones";
	import { EntityType } from "@/types/EntityType";
	import { blurActiveElement, calculateTimeout } from "@/helpers/utilities";
	import { shouldLetBrowserHandleClick } from "@/helpers/linkNavigation";
	import { deleteArtifacts, getArtifactTypeConfig, tagArtifacts } from "@/helpers/artifacts";
	import { Artifact } from "@/helpers/artifacts";
	import { RecordingStatus } from "@/types/artifacts/Recordings";
	import { Label } from "@/types/Label";
	import { isIncompleteRecordingUpload } from "@/helpers/incompleteRecordingUpload";
	import VideoPreview from "@/components/common/VideoPreview.vue";
	import DocumentTile from "@/components/search/DocumentTile.vue";
	import { getTimelineDocumentDownloadName, isPdfTimelineDocument, isTimelineDocument } from "@/helpers/timelineDocuments";
	import SeanIcon from "@/components/icons/SeanIcon.vue";
	import TagSelectionDialog from "@/components/tags/TagSelectionDialog.vue";
	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";
	import api from "@/api";

	const isDebugMode = new URLSearchParams(window.location.search).get("debug") === "true";

	const props = defineProps({
		mediaItems: {
			type: Array<IndexableDocument>,
			default: () => []
		},
		count: {
			type: Number,
			default: 0
		},
		countCap: {
			type: Number,
			default: Number.POSITIVE_INFINITY
		},
		loading: {
			type: Boolean,
			default: false
		},
		searchTerm: {
			type: String,
			default: ""
		},
		// LSP-20939: the displayed results were narrowed by user filters (not counting the search term), so an
		// empty result means "nothing matched", not "this session has no media".
		filtersApplied: {
			type: Boolean,
			default: false
		},
		parentType: {
			type: String as PropType<EntityType | null>,
			default: "search",
			required: false
		},
		parentId: {
			type: String,
			default: "",
			required: false
		},
		parentName: {
			type: String,
			default: "search",
			required: false
		},
		isViewOnly: {
			type: Boolean,
			default: false
		},
		hasFreeTrialEnded: {
			type: Boolean,
			default: false
		},
		isAuthenticated: {
			type: Boolean,
			default: false
		},
		pendingImages: {
			type: Number,
			default: 0
		},
		pendingDocs: {
			type: Number,
			default: 0
		},
		pendingVideos: {
			type: Number,
			default: 0
		},
		pendingMediaCount: {
			type: Number,
			default: 0
		},
		pendingRecordingCount: {
			type: Number,
			default: 0
		},
		/**
		 * LSP-15573: server count of admin video FILE uploads in this session that started and never
		 * finished, and that the pending counts have already aged out (`countAbandonedVideoFileUploads`).
		 * Distinct from `pendingRecordingCount`, which means "still arriving" — this one means "gave up".
		 */
		failedVideoUploadCount: {
			type: Number,
			default: 0
		},
		/**
		 * LSP-19565: whether the current account can run Sparky reports on this entity. Gates the
		 * "Run a Spark on N selected" selection action; mirrors the header "Run a Spark" gate.
		 */
		hasScribeFeature: {
			type: Boolean,
			default: false
		},
		/**
		 * LSP-16659: the section heading. Timeline is shared with global search, which keeps "Media"; the
		 * Session and Project pages pass "Media & Documents" because their grid holds documents too.
		 */
		heading: {
			type: String,
			default: "Media"
		},
		/** LSP-16659: an optional note after the count ("· Showing documents" / "· Filtered"). Hidden in select mode. */
		headingSuffix: {
			type: String,
			default: ""
		}
	});

	const emit = defineEmits<{
		(e: "page"): void;
		(e: "reload"): void;
		(e: "item-click", id: string): void;
		// LSP-16659: a document tile was clicked outside select mode. Documents never go to the lightbox —
		// the parent opens the PDF viewer, or opens/downloads any other file.
		(e: "document-click", item: IndexableDocument): void;
		(e: "on-selected-items", items: SelectedMediaItem[]): void;
		(e: "on-deleted-items", items: SelectedMediaItem[], deleted: boolean): void;
		(e: "update:mediaItems", items: IndexableDocument[]): void;
		(e: "createSheet"): void;
		(e: "runSparkyReport"): void;
		(e: "selection-cancelled"): void;
	}>();

	const mediaGridContainer = ref<HTMLElement | null>(null);
	const paginationSentinel = ref<HTMLElement | null>(null);
	const observer = ref<IntersectionObserver | null>(null);
	const selectedMediaItems = ref<SelectedMediaItem[]>([]);
	const selectingAll = ref<boolean>(false);
	let selectionRevision = 0;
	let selectionPruneDeferred = false;
	// Upper bound on how long we wait for one page before giving up on Select All.
	const PAGE_WAIT_TIMEOUT_MS = 15000;
	const selecting = ref<boolean>(false);

	defineExpose({
		scrollToTop,
		toggleSelecting,
		cancelChecklistSelection,
		selectedMediaItems
	});
	const downloading = ref<boolean>(false);
	const downloadingDay = ref<string | null>(null);
	const instance = getCurrentInstance();
	// Seed from props.count synchronously (not only in onMounted) so computeds and the diagnostic watch that
	// read mediaCount at setup/immediate time see the server count, not a transient 0. The props.count
	// sync watch and onMounted assignment below keep it current thereafter.
	const mediaCount = ref<number>(props.count ?? 0);

	// Recordings still transcoding can appear in the result count (`count`) but aren't playable yet, so
	// we subtract them from the header "Media (N)" to reflect only viewable media. Only subtract the
	// processing recordings that are ACTUALLY in the loaded timeline (i.e. already counted in `count`) —
	// a DB-pending recording that is not search-eligible isn't in `count`, so subtracting it would drop the total
	// below the number of items actually shown. Cap by the DB pending count and clamp at 0. LSP-16121.
	const processingRecordingsInList = computed(
		() => props.mediaItems.filter((item) => item.entityType === "recording" && !isVideoSelectable(item)).length
	);
	const visibleMediaCount = computed(() => {
		const subtract = Math.min(props.pendingRecordingCount, processingRecordingsInList.value);
		return Math.max(0, mediaCount.value - subtract);
	});
	const displayedMediaCount = computed(() => Math.min(visibleMediaCount.value, props.countCap));
	const mediaCountCapped = computed(() => mediaCount.value > props.countCap);
	const selectingChecklistMedia = ref<boolean>(false);
	const bulkTagDialogVisible = ref<boolean>(false);
	const display = useDisplay();
	const toast = useToast();
	const me = ref<any>(null);
	const dayView = ref<boolean>(false);
	const draggedItem = ref<IndexableDocument | null>(null);
	const dragOverDateKey = ref<string | null>(null);
	const DAY_VIEW_DRAG_MIME_TYPE = "application/x-liveswitch-day-view-item-id";

	const dayViewDisabled = computed(() => {
		return !!props.searchTerm;
	});

	// Server-derived count of recordings still uploading/transcoding that are NOT already represented on
	// screen. An uploaded video becomes search-eligible only after enough processing state is committed,
	// so a just-uploaded/processing video can be absent from the timeline list (mediaItems)
	// and to the client refs after a reload. We render a skeleton for it from the authoritative server count
	// so the session shows a processing placeholder instead of "There's no media here yet". Subtract the
	// representations that already exist so we never double-render the same pending recording: the
	// client-side in-flight videos (current-tab uploads) AND the processing recordings already in the loaded
	// list (returned-but-not-yet-playable, mirroring the visibleMediaCount reconciliation). Clamp at 0.
	// LSP-15561 / LSP-15562 / LSP-15571.
	const serverPendingVideoPlaceholders = computed(() => {
		return Math.max(0, props.pendingRecordingCount - props.pendingVideos - processingRecordingsInList.value);
	});

	const hasPendingVisualMedia = computed(() => {
		// LSP-16659: an uploading document now draws a skeleton in this grid too, so it counts as on-screen
		// pending media for the empty-state and the day-view "today" group.
		return props.pendingImages > 0 || props.pendingVideos > 0 || props.pendingDocs > 0 || serverPendingVideoPlaceholders.value > 0;
	});

	// break-it round 1: the prop is declared `type: Number`, but Vue does not enforce that at runtime and
	// this count is rendered verbatim to the user. A non-integer (NaN, Infinity, a numeric string that
	// slipped past the parent's own normalization) would print an unusable message on the one surface whose
	// job is to explain a failure clearly, so anything that is not a safe positive integer counts as none.
	// Deliberately does NOT coerce a string: the API boundary in EntityDetailPage owns coercion, and a
	// string reaching a Number prop is a caller bug rather than a server-shape quirk.
	// Dedupe for the malformed-prop diagnostic below — plain Set, nothing renders from it.
	const reportedMalformedFailedCounts = new Set<string>();

	const trustedFailedVideoUploadCount = computed(() => {
		const reported = props.failedVideoUploadCount;
		if (reported === 0) return 0;
		if (typeof reported !== "number" || !Number.isSafeInteger(reported) || reported < 0) {
			// Review symmetry fix: rejecting the prop makes the tile silently never appear, which on screen is
			// indistinguishable from "no failed uploads". EntityDetailPage normalizes (and logs) before this
			// today, but Timeline has other callers, so the branch has to name itself. Deduped per value
			// because this is a computed re-evaluated on every poll.
			if (!reportedMalformedFailedCounts.has(String(reported))) {
				reportedMalformedFailedCounts.add(String(reported));
				console.warn(
					`Timeline: ignoring a malformed failedVideoUploadCount prop (${typeof reported}: ${String(reported)}, ` +
						`parentType=${props.parentType}); the failed-upload state will not render for it.`
				);
			}
			return 0;
		}
		return reported;
	});

	/**
	 * LSP-15573: render an explicit "this upload didn't finish" state instead of "There's no media here
	 * yet" when the server reports an admin video file upload that started and never completed.
	 *
	 * Every guard here is load-bearing:
	 *  - `mediaCount === 0` — the abandoned RecordingSession row is never resolved (a retry creates a NEW
	 *    session), so an unconditional tile would strand a permanent phantom placeholder beside the video
	 *    that replaced it. Requiring an empty timeline is what makes a successful re-upload clear it.
	 *  - `!hasPendingVisualMedia` — a live upload already draws the grey "still processing" skeleton;
	 *    two placeholders for one upload is worse than none, so the in-flight signal wins.
	 *  - `!loading` / `!searchTerm` / `!filtersApplied` — those empty-states describe the request, not the session's media,
	 *    and replacing them would be a lie about what the user just asked for.
	 */
	const showFailedVideoUploadState = computed(() => {
		return (
			mediaCount.value === 0 &&
			// break-it round 2: `mediaCount` is a ref seeded from `count` and re-synced by a watcher, so a
			// caller that applies a response in pieces (items first, aggregate count a tick later) leaves it
			// at 0 while real media is already rendering. The loaded list is the stronger evidence that
			// media exists, so an item on screen retires the failed-upload state immediately rather than one
			// reactive tick later — otherwise the retried video and the "didn't finish" tile appear together.
			props.mediaItems.length === 0 &&
			!hasPendingVisualMedia.value &&
			!props.loading &&
			!props.searchTerm &&
			!props.filtersApplied &&
			trustedFailedVideoUploadCount.value > 0
		);
	});

	const failedVideoUploadMessage = computed(() => {
		const count = trustedFailedVideoUploadCount.value;
		return `${count} video upload${count === 1 ? "" : "s"} didn't finish`;
	});

	/**
	 * break-it round 2: "Please try uploading again" is an instruction, and an instruction aimed at someone
	 * who cannot act on it is worse than silence — a view-only viewer (a shared session link, authenticated
	 * or not) has no upload control to use. They still get the FACT that an upload failed, because that is
	 * what explains the empty session to them; they just do not get told to fix it.
	 *
	 * Keyed to `isViewOnly` alone, matching how the rest of this component gates write affordances. The
	 * unauthenticated case rides along because EntityDetailPage sets view-only for those viewers; adding
	 * `!isAuthenticated` as a second condition would instead suppress the instruction for every caller that
	 * leaves the prop at its `false` default, including the ordinary signed-in session page.
	 */
	const failedVideoUploadDetail = computed(() => {
		if (props.isViewOnly) {
			return "Nothing was saved to this session.";
		}
		return "Nothing was saved to this session. Please try uploading again.";
	});

	const timelineTimezone = moment.tz.guess();
	const todayDateKey = computed(() => {
		return moment().tz(timelineTimezone).format("YYYY-MM-DD");
	});

	const todayDateLabel = computed(() => {
		return moment().tz(timelineTimezone).format(getDateTokens(timezones.getDateFormat()).weekdayFullMonthDayOrdinal);
	});

	const groupedMediaByDate = computed(() => {
		if (!dayView.value) {
			return [];
		}

		const groups = groupMediaByDate(props.mediaItems, timezones.getDateFormat());
		if (!hasPendingVisualMedia.value) {
			return groups;
		}

		const hasTodayGroup = groups.some((group) => group.dateKey === todayDateKey.value);
		if (hasTodayGroup) {
			return groups;
		}

		const todayGroup: DayGroup = {
			dateKey: todayDateKey.value,
			dateLabel: todayDateLabel.value,
			items: []
		};

		const undatedGroup = groups.find((group) => group.dateKey === UNDATED_DATE_KEY);
		const datedGroups = groups.filter((group) => group.dateKey !== UNDATED_DATE_KEY);
		datedGroups.push(todayGroup);
		datedGroups.sort((a, b) => b.dateKey.localeCompare(a.dateKey));

		return undatedGroup ? [...datedGroups, undatedGroup] : datedGroups;
	});

	const dateLabel = computed(() => {
		// Empty-state must honor the SERVER pending counts, not only the client in-flight refs — a video
		// uploaded in a prior tab/session is a processing RecordingSession not yet search-eligible, so
		// mediaCount and the client refs are all 0 while pendingRecordingCount is > 0. Reuse the single
		// hasPendingVisualMedia predicate so the empty-state can never drift from the skeleton-render
		// gate (the original divergence that caused this bug). LSP-15561/15562/15571.
		if (mediaCount.value == 0 && !hasPendingVisualMedia.value) {
			if (props.loading) {
				return "Loading...";
			}
			// LSP-20922: a search that matched nothing names the term in the shared list phrasing
			// (listEmptyMessage, LSP-19486) — "There's no media here yet." told someone who had just searched
			// that their photos were gone. Checked before the failed-upload state below on purpose: with a term
			// applied, the empty result describes the search, not the session (see showFailedVideoUploadState).
			// LSP-20939: filters that narrowed the session to nothing get the same treatment, in the same slot.
			const appliedSearchTerm = (props.searchTerm ?? "").trim();
			if (appliedSearchTerm || props.filtersApplied) {
				return listEmptyMessage("media", appliedSearchTerm, { filtersApplied: props.filtersApplied });
			}
			// LSP-15573: an upload that started and died is not "no media here yet" — the failed-upload
			// tile below carries the honest state and its own copy, so this label steps aside for it.
			if (showFailedVideoUploadState.value) {
				return "";
			}
			return `There's no media here yet.`;
		}

		return "";

		// TODO: Leaving this for now in case we need to re-enable it later.
		// const sortedDates = [...visibleDates.value].sort();
		// let label = "";

		// if (sortedDates.length == 0) {
		// 	if (props.searchTerm) {
		// 		return `No results found for "${props.searchTerm}".`;
		// 	}
		// 	return "No data available.";
		// }

		// if (sortedDates.length == 1) {
		// 	label = formatDate(sortedDates[0]);
		// } else {
		// 	label = `${formatDate(sortedDates[0])} - ${formatDate(sortedDates[sortedDates.length - 1])}`;
		// }

		// return label;
	});

	const dateLabelClass = computed(() => {
		if (mediaCount.value == 0) {
			return "justify-center align-center no-data";
		}

		return "content-date-header";
	});

	const showSessionNumber = computed(() => {
		return me.value && props.parentType !== "conversation";
	});

	const showActionButtons = computed(() => {
		return (
			!props.isViewOnly &&
			!props.hasFreeTrialEnded &&
			props.isAuthenticated &&
			selectedMediaItems.value.length > 0 &&
			!selectingChecklistMedia.value
		);
	});
	/**
	 * LSP-16659: Sparky analyzes photos, videos and PDFs only, so "Run a Spark on N selected" is
	 * offered only when the selection holds at least one of those — a selection of other files (e.g.
	 * only .docx) keeps Sparky unavailable, matching the page-level gate. Those files stay selectable for
	 * download, tag and delete. Classified from the live row (it carries the hydrated mime type), falling
	 * back to the selection's own name / URL when the row has scrolled out of the loaded list.
	 */
	const selectionHasSparkyAnalyzableMedia = computed(() => {
		return selectedMediaItems.value.some((selectedItem) => {
			if (selectedItem.type !== ArtifactType.Files) return true;
			const identity = selectedItem.searchResultId ?? selectedItem.id;
			const liveRow = props.mediaItems.find((mediaItem) => resultIdentity(mediaItem) === identity);
			return isPdfTimelineDocument(
				liveRow ?? ({ id: selectedItem.id, name: selectedItem.name, mediaUrl: selectedItem.mediaUrl } as IndexableDocument)
			);
		});
	});

	const canEditArtifactDates = computed(() => {
		return !props.isViewOnly && !props.hasFreeTrialEnded && props.isAuthenticated;
	});

	function showPendingPlaceholdersInGroup(groupDateKey: string) {
		return hasPendingVisualMedia.value && groupDateKey === todayDateKey.value;
	}

	const disableDeleteButton = computed(() => {
		return downloading.value;
	});

	const selectableMediaItemsCount = computed(() => {
		return new Set(props.mediaItems.filter((item) => isItemSelectable(item)).map((item) => resultIdentity(item))).size;
	});
	const hasPotentialSelectableMediaItems = computed(() => {
		return selectableMediaItemsCount.value > 0 || props.mediaItems.length < props.count;
	});
	const allSelectableMediaItemsSelected = computed(() => {
		if (props.mediaItems.length !== props.count) return false;
		const selectedIdentities = new Set(selectedMediaItems.value.map((item) => item.searchResultId ?? item.id));
		return (
			selectableMediaItemsCount.value > 0 &&
			selectedIdentities.size === selectableMediaItemsCount.value &&
			props.mediaItems.filter((item) => isItemSelectable(item)).every((item) => selectedIdentities.has(resultIdentity(item)))
		);
	});

	// Auto-disable day view when search term is entered
	watch(
		() => props.searchTerm,
		(newVal) => {
			if (newVal) {
				dayView.value = false;
			}
		}
	);

	// Diagnostic logging for the two silent paths around a server-pending recording with no indexed media
	// and no client-side in-flight uploads. Kept in a watch (NOT in the dateLabel computed, which must stay
	// pure and is re-read every render) so each transition logs at most once instead of on every poll-driven
	// recompute. Two fail-conditions per the silent-failure rule (CLAUDE.md): (1) a server-pending skeleton
	// IS shown — the empty-state is suppressed; (2) the server still reports a pending recording but the
	// placeholder count clamped to 0 (fully subtracted) so the empty-state "There's no media here yet" shows
	// anyway — the genuinely stuck/silent case a future incident would otherwise have to reconstruct.
	// LSP-15561 / LSP-15562 / LSP-15571.
	watch(
		() => mediaCount.value == 0 && props.pendingImages == 0 && props.pendingVideos == 0 && props.pendingRecordingCount > 0,
		(serverPendingWithNothingIndexed) => {
			if (!serverPendingWithNothingIndexed) {
				return;
			}
			if (serverPendingVideoPlaceholders.value > 0) {
				console.warn(
					`Timeline: suppressing empty-state, showing placeholder for server-pending recordings (pendingRecordingCount=${props.pendingRecordingCount}, pendingMediaCount=${props.pendingMediaCount}, parentType=${props.parentType})`
				);
			} else {
				console.warn(
					`Timeline: server reports pending recordings but placeholder count clamped to 0 — empty-state shown despite pending media (pendingRecordingCount=${props.pendingRecordingCount}, pendingVideos=${props.pendingVideos}, processingRecordingsInList=${processingRecordingsInList.value}, parentType=${props.parentType})`
				);
			}
		},
		{ immediate: true }
	);

	// LSP-15573 diagnostic (silent-failure rule, CLAUDE.md). The failed-upload state exists precisely
	// because an abandoned admin video upload has NO other trace on this page — so every branch of the
	// decision has to be attributable from the log alone, including the ones that decline to show it.
	// Kept in a watch, not the computed, for the same reason as the watch above: the computed re-runs on
	// every poll-driven recompute and an inline warn would bury itself.
	// break-it round 1: watching only the trigger missed the transition that matters most — a live upload
	// starting up and outranking a failed-upload state that was ALREADY on screen. The trigger boolean does
	// not change across that transition, so the watch never re-fired and the placeholder disappeared with
	// nothing in the log to say why. Watch the DECISION alongside the trigger so every change of outcome is
	// reported, not just the moment the trigger first becomes true.
	watch(
		() => [
			mediaCount.value === 0 &&
				props.mediaItems.length === 0 &&
				!props.loading &&
				!props.searchTerm &&
				!props.filtersApplied &&
				trustedFailedVideoUploadCount.value > 0,
			showFailedVideoUploadState.value
		],
		([abandonedUploadOnEmptyTimeline, showingFailedState], previous) => {
			// Review: the early return used to swallow the DISAPPEARANCE direction. The tile going away is a
			// state change an operator has to be able to explain — the count can drop to 0 because the server
			// count failed open on a DB error, because a live heartbeat resumed the upload, because a playable
			// recording finally appeared, or because a filter narrowed the page — and none of those left a
			// client-side trace. The watch's own contract says every branch of the decision is attributable
			// from the log alone; this is the branch that was not.
			if (!abandonedUploadOnEmptyTimeline) {
				// Review: the empty-timeline gate is the OTHER way the tile is withheld — a session holding a
				// failed upload alongside real media shows nothing about it, by design (an abandoned session row
				// is never resolved, so an unconditional tile would strand a phantom). Designed or not, the
				// watch's contract is that every branch is attributable, and this one said nothing. Deduped per
				// count so a page that simply has media does not repeat it on every poll.
				if (trustedFailedVideoUploadCount.value > 0 && (mediaCount.value > 0 || props.mediaItems.length > 0)) {
					const coexistKey = `coexist:${trustedFailedVideoUploadCount.value}`;
					if (!reportedMalformedFailedCounts.has(coexistKey)) {
						reportedMalformedFailedCounts.add(coexistKey);
						console.warn(
							`Timeline: abandoned upload(s) reported but withheld because this session already shows media (failedVideoUploadCount=${trustedFailedVideoUploadCount.value}, mediaCount=${mediaCount.value}, mediaItems=${props.mediaItems.length}, parentType=${props.parentType})`
						);
					}
				}
				if (previous?.[1]) {
					console.warn(
						`Timeline: failed-upload state retired — it was on screen and is not any more (failedVideoUploadCount=${trustedFailedVideoUploadCount.value}, mediaCount=${mediaCount.value}, mediaItems=${props.mediaItems.length}, loading=${props.loading}, searchTerm=${props.searchTerm ? "set" : "none"}, filtersApplied=${props.filtersApplied}, parentType=${props.parentType})`
					);
				}
				return;
			}
			if (showingFailedState) {
				console.warn(
					`Timeline: suppressing empty-state, showing failed-upload state for abandoned admin video uploads (failedVideoUploadCount=${trustedFailedVideoUploadCount.value}, parentType=${props.parentType})`
				);
			} else {
				// The only way to reach here: hasPendingVisualMedia is true, so a live upload's skeleton is
				// already on screen and deliberately outranks this state. Logged anyway — "the failed-upload
				// tile did not appear" must never be a silent outcome.
				console.warn(
					`Timeline: server reports abandoned video uploads but the in-flight processing placeholder outranks the failed-upload state (failedVideoUploadCount=${trustedFailedVideoUploadCount.value}, pendingImages=${props.pendingImages}, pendingVideos=${props.pendingVideos}, serverPendingVideoPlaceholders=${serverPendingVideoPlaceholders.value}, parentType=${props.parentType})`
				);
			}
		},
		{ immediate: true }
	);

	onMounted(async () => {
		mediaCount.value = props.count;
		initializeObserver();
		if (props.isAuthenticated) {
			try {
				me.value = await api.getMe();
			} catch (err) {
				console.error("Failed to get me", err);
			}
		}
	});

	onUnmounted(() => {
		clearDayViewDragState();
		disconnectObserver();
	});

	watch(
		() => [props.mediaItems, ...props.mediaItems.map(resultIdentity)],
		() => {
			refreshObservedItems();

			if (downloading.value) {
				if (!selectionPruneDeferred) {
					console.warn(
						"Timeline.mediaItemsWatch: media changed while downloading; deferring selection pruning until the download finishes",
						{
							parentType: props.parentType,
							parentId: props.parentId,
							selectedItems: selectedMediaItems.value.length,
							loadedMediaItems: props.mediaItems.length
						}
					);
				}
				selectionPruneDeferred = true;
				return;
			}

			pruneSelectedMediaItems();
		}
	);

	watch(
		dayView,
		(isDayViewEnabled) => {
			if (!isDayViewEnabled) {
				clearDayViewDragState();
			}
			refreshObservedItems();
		},
		{ flush: "post" }
	);

	watch(
		() => props.count,
		(newValue) => {
			mediaCount.value = newValue;
		},
		{ deep: true, immediate: true }
	);

	function refreshObservedItems() {
		observer.value?.disconnect();
		observeItems();
	}

	// TODO: Refactor into a separate class.
	function initializeObserver() {
		observer.value = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) page();
			},
			{
				threshold: 0, // triggers as soon as element touches viewport
				root: props.parentType === "conversation" || props.parentType === "project" ? null : mediaGridContainer.value, // optional: only if it's a scrollable div
				rootMargin: "0px 0px 0px 0px"
			}
		);

		observeItems();
	}

	function observeItems() {
		nextTick(() => {
			if (paginationSentinel.value) observer.value?.observe(paginationSentinel.value);
		});
	}

	function disconnectObserver() {
		if (observer.value) {
			observer.value.disconnect();
			observer.value = null;
		}
	}

	function formatDate(date: string) {
		const formatted = moment(date).format(getDateTokens(timezones.getDateFormat()).dateShortMonthOrdinal);
		return formatted;
	}

	function getImageUrl(mediaItem: IndexableDocument) {
		return mediaItem.thumbnailUrl ?? mediaItem.mediaUrl;
	}

	function scrollToTop() {
		mediaGridContainer.value?.scrollTo({ top: 0, behavior: "instant" });
	}

	function page() {
		emit("page");
	}

	function onItemClick(id: string) {
		emit("item-click", id);
	}

	function handleMediaClick(mediaItem: IndexableDocument) {
		if (selecting.value && isItemSelectable(mediaItem)) {
			toggleItem(mediaItem);
		} else if (isTimelineDocument(mediaItem)) {
			// LSP-16659: documents never reach MediaLightBox (it cannot render them, and its v-model write-back
			// would own their rows); the parent opens its PDF viewer or the file itself.
			emit("document-click", mediaItem);
		} else {
			onItemClick(resultIdentity(mediaItem));
		}
	}

	// Bulk selection

	function toggleSelecting(evt: Event | null, selectForChecklist: boolean = false) {
		blurActiveElement();

		// Cancelling an in-progress checklist media selection (parent must reset
		// ProjectChecklists' attach state so "Add Checklist Item" re-appears) — LSP-16140
		const cancellingChecklistSelection = selecting.value && selectingChecklistMedia.value && !selectForChecklist;

		// If already selecting and we want to select for checklist, just update the mode
		if (selecting.value && selectForChecklist) {
			selectingChecklistMedia.value = true;
			return;
		}

		selecting.value = !selecting.value;
		selectionRevision += 1;
		selectingChecklistMedia.value = selectForChecklist;

		if (!selecting.value) {
			selectedMediaItems.value = [];
			if (cancellingChecklistSelection) emit("selection-cancelled");
		}
	}

	// LSP-17252: a parent-driven cancel (the target checklist was removed mid-add) must exit
	// checklist-selection mode the same way the user's own Cancel button does — but unlike
	// toggleSelecting, which unconditionally flips `selecting`, this is a no-op unless a
	// checklist selection is actually in progress. That guard matters because the caller
	// (EntityDetailPage) cannot itself know whether `selecting`/`selectingChecklistMedia` are
	// still true by the time its event handler runs (e.g. the user's own Cancel click could
	// have already raced ahead of it) — calling the unconditional toggleSelecting here would
	// incorrectly turn selection mode BACK ON in that race.
	function cancelChecklistSelection() {
		if (selecting.value && selectingChecklistMedia.value) {
			toggleSelecting(null, false);
			return;
		}
		// LSP-17252 review round: this is the guard described above — log it so a future "the
		// checklist got removed but this Timeline never emitted selection-cancelled" report is
		// traceable to "there was nothing to cancel here" rather than requiring reconstruction.
		console.warn("Timeline.cancelChecklistSelection: no checklist selection was in progress — nothing to cancel", {
			parentType: props.parentType,
			parentId: props.parentId,
			selecting: selecting.value,
			selectingChecklistMedia: selectingChecklistMedia.value
		});
	}

	/**
	 * Resolves once mediaItems grows past the length we paged against, or the wait times out.
	 * The owner loads pages asynchronously in response to the "page" event, so there is nothing
	 * to await directly — we watch the prop instead.
	 */
	function waitForMoreMediaItems(loadedLengthBeforeRequest: number): Promise<void> {
		return new Promise((resolve) => {
			let settled = false;
			const settle = () => {
				if (settled) return;
				settled = true;
				stopWatching();
				clearTimeout(timeoutId);
				resolve();
			};
			const stopWatching = watch(
				() => props.mediaItems.length,
				(length) => {
					if (length > loadedLengthBeforeRequest) settle();
				}
			);
			// A page that errors or returns nothing must not hang Select All forever.
			const timeoutId = setTimeout(settle, PAGE_WAIT_TIMEOUT_MS);
		});
	}

	async function selectAll() {
		// Re-entry would run two paging loops against the same list.
		if (selectingAll.value) return;
		selectingAll.value = true;
		const requestSelectionRevision = selectionRevision;
		let allMediaItems: readonly IndexableDocument[] = [];

		try {
			// LSP-18421: the list is paged, so the loaded slice is only what has been scrolled
			// into view. Load the rest before selecting, otherwise "Select All" silently means
			// "select the first page".
			allMediaItems = await loadAllPages({
				getLoaded: () => props.mediaItems,
				getTotal: () => props.count ?? 0,
				requestPage: () => page(),
				waitForMore: waitForMoreMediaItems
			});
		} finally {
			selectingAll.value = false;
		}

		// cursor review: paging awaits a network round trip, and the user can leave selection mode
		// (Cancel / Done) while it runs. Cancel has already cleared selectedMediaItems and set
		// selecting false, so writing the full set now would restore a hidden selection that
		// reappears the next time Select is opened. Bail instead of resurrecting it.
		if (!selecting.value || selectionRevision !== requestSelectionRevision) {
			console.warn("Timeline.selectAll: selection changed while paging; leaving the current selection unchanged", {
				parentType: props.parentType,
				parentId: props.parentId,
				selecting: selecting.value,
				selectionRevision,
				requestSelectionRevision
			});
			return;
		}

		const countIsInteger = Number.isInteger(props.count);
		const countIsNonNegative = props.count >= 0;
		const countMatchesLoaded = props.count === allMediaItems.length;
		if (!countIsInteger || !countIsNonNegative || !countMatchesLoaded) {
			console.warn("Timeline.selectAll: loaded media does not match the requested total; leaving selection unchanged", {
				parentType: props.parentType,
				parentId: props.parentId,
				totalCount: props.count,
				loadedMediaItems: allMediaItems.length,
				countIsInteger,
				countIsNonNegative,
				countMatchesLoaded
			});
			const message = "We couldn't select all media. Please try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
			return;
		}

		selectedMediaItems.value = Array.from(
			new Map(
				allMediaItems.filter((item) => isItemSelectable(item)).map((item) => [resultIdentity(item), toSelectedMediaItem(item)])
			).values()
		) as SelectedMediaItem[];
	}

	function deselectAll() {
		selectionRevision += 1;
		selectedMediaItems.value = [];
	}

	function doneSelecting() {
		emit("on-selected-items", selectedMediaItems.value);
		resetSelectedMediaItems();
	}

	function isVideoSelectable(mediaItem: IndexableDocument) {
		// LSP-16659: a document row is always ready — the server excludes pending and unsignable files from
		// the media search — so it no longer rides on the parent-status READY coincidence below.
		if (isTimelineDocument(mediaItem)) return true;
		return (
			mediaItem.entityType === "snapshot" ||
			(mediaItem.entityType === "recording" && mediaItem.status?.toUpperCase() === RecordingStatus.Completed) ||
			mediaItem.status?.toUpperCase() === RecordingStatus.Ready
		);
	}

	/**
	 * LSP-16659: whether this tile can join the CURRENT selection. Documents cannot be attached to a
	 * checklist (attach resolves only photo and video ids), so they sit out checklist-select mode.
	 */
	function isItemSelectable(mediaItem: IndexableDocument) {
		if (isTimelineDocument(mediaItem) && selectingChecklistMedia.value) return false;
		return isVideoSelectable(mediaItem);
	}

	function unselectableReason(mediaItem: IndexableDocument) {
		return isTimelineDocument(mediaItem) ? "Documents can't be added to a checklist" : "This video is still processing";
	}

	function toSelectedMediaItem(item: IndexableDocument): SelectedMediaItem {
		return {
			id: resultIdentity(item),
			searchResultId: resultIdentity(item),
			legacyId: item.id,
			name: isTimelineDocument(item) ? getTimelineDocumentDownloadName(item) : null,
			conversationId: item.conversationId,
			projectId: item.project?.id,
			organizationId: item.organizationId,
			mediaUrl: item.mediaUrl,
			thumbnailUrl: item.thumbnailUrl ?? null,
			// LSP-16659: documents are Files. They used to fall through to Recordings, which would have sent
			// File ids to the recordings bulk-delete and bulk-tag routes.
			type:
				item.entityType === "snapshot"
					? ArtifactType.Snapshots
					: isTimelineDocument(item)
						? ArtifactType.Files
						: ArtifactType.Recordings
		};
	}

	function getSelectedItemsWithDates(items: SelectedMediaItem[]) {
		return items.map((item) => {
			const identity = item.searchResultId ?? item.id;
			const mediaItem = props.mediaItems.find((m) => resultIdentity(m) === identity);
			if (!mediaItem) {
				// An empty `created` feeds the bulk-download date bucketing, which then files the item
				// under an unnamed bucket. Same root cause as setLocalArtifactDate: the live list moved
				// under a selection that was captured earlier.
				console.warn("Timeline.getSelectedItemsWithDates: selected item is no longer in the live media list", {
					identity,
					loadedMediaItems: props.mediaItems.length
				});
			}
			return { ...item, created: mediaItem?.created ?? "" };
		});
	}

	async function downloadSelectedItems(items: SelectedMediaItem[], resetSelectionOnSuccess = false) {
		if (items.length > 50) {
			const message = "You can only download up to 50 items at a time.";
			toast.error(message, { timeout: calculateTimeout(message) });
			return;
		}

		const submittedSelectionRevision = selectionRevision;
		try {
			downloading.value = true;
			const parentName = props.parentName;
			const parentId = props.parentId;
			const currentItems = items.map((item) => {
				const identity = item.searchResultId ?? item.id;
				const currentItem = props.mediaItems.find((mediaItem) => resultIdentity(mediaItem) === identity);
				return currentItem ? toSelectedMediaItem(currentItem) : item;
			});

			// When in day view, use date-based folder structure
			if (dayView.value) {
				const itemsWithDates = getSelectedItemsWithDates(currentItems);
				await downloadMediaFilesByDate(itemsWithDates, parentId, parentName);
			} else {
				const downloadStarted = await downloadMediaFiles(currentItems, parentId, parentName);
				if (downloadStarted === false) {
					throw new Error("The selected media download could not be started");
				}
			}

			const selectionUnchanged =
				selectionRevision === submittedSelectionRevision &&
				selectedMediaItems.value.length === items.length &&
				selectedMediaItems.value.every(
					(item, index) => (item.searchResultId ?? item.id) === (items[index].searchResultId ?? items[index].id)
				);
			if (resetSelectionOnSuccess) {
				if (selectionUnchanged) {
					resetSelectedMediaItems();
				} else {
					console.warn(
						"Timeline.downloadSelectedItems: selection changed while downloading; leaving the current selection unchanged",
						{
							parentType: props.parentType,
							parentId: props.parentId,
							submittedItems: items.length,
							currentSelectedItems: selectedMediaItems.value.length,
							submittedSelectionRevision,
							currentSelectionRevision: selectionRevision
						}
					);
				}
			}
		} catch (err) {
			const message = "We couldn't download all selected media. Your selection is still available so you can try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
			console.error(err);
			console.error(err, {
				tags: {
					parentType: props.parentType,
					parentId: props.parentId,
					parentName: props.parentName
				},
				extra: {
					file: "Timeline.vue",
					method: "downloadSelectedItems"
				}
			});
		} finally {
			downloading.value = false;
			if (selectionPruneDeferred) {
				selectionPruneDeferred = false;
				pruneSelectedMediaItems();
			}
		}
	}

	async function downloadMediaItems() {
		await downloadSelectedItems([...selectedMediaItems.value], true);
	}

	async function downloadDay(group: DayGroup) {
		// LSP-16659: documents belong to the day too, so "Download Day" includes them.
		const dayItems = group.items
			.filter(
				(item) =>
					(item.entityType === "snapshot" || item.entityType === "recording" || isTimelineDocument(item)) &&
					isVideoSelectable(item)
			)
			.map((item) => toSelectedMediaItem(item));

		if (dayItems.length === 0) return;

		try {
			downloadingDay.value = group.dateKey;
			await downloadSelectedItems(dayItems, false);
		} finally {
			downloadingDay.value = null;
		}
	}

	async function deleteMediaItems() {
		// Get the config for proper naming in dialog
		const config = getArtifactTypeConfig(ArtifactType.Content);
		const isPlural = selectedMediaItems.value.length > 1;
		const itemName = isPlural ? config.pluralName : config.singularName;
		const confirmed = await (instance?.proxy as any).$root.$confirm.deleteOpen(
			`Delete ${isPlural ? itemName : config.singularName}`,
			`This will delete the ${selectedMediaItems.value.length} selected ${itemName}.`
		);

		if (!confirmed) {
			return;
		}

		selecting.value = false;

		const submittedItems = [...selectedMediaItems.value];
		const artifacts = submittedItems.map((x) => ({
			id: x.id,
			conversationId: x.conversationId
		})) as Artifact[];
		// One delete per artifact type (documents are Files since LSP-16659), settled independently so a
		// failure in one type neither hides the others' success nor leaves the grid showing deleted rows.
		const deletions = [ArtifactType.Snapshots, ArtifactType.Recordings, ArtifactType.Files]
			.map((artifactType) => ({ artifactType, items: submittedItems.filter((x) => x.type === artifactType) }))
			.filter((deletion) => deletion.items.length > 0);

		const results = await Promise.allSettled(
			deletions.map((deletion) =>
				deleteArtifacts(
					deletion.artifactType,
					deletion.items.map((x) => x.id),
					artifacts
				)
			)
		);
		const deletedItems = deletions.flatMap((deletion, index) => (results[index].status === "fulfilled" ? deletion.items : []));
		const failedDeletions = deletions.filter((_, index) => results[index].status === "rejected");

		if (deletedItems.length > 0) {
			emit("on-deleted-items", deletedItems, true);
		}
		// Select mode is already closed; clear the selection either way so a failed delete does not
		// resurface as a hidden selection the next time Select is opened.
		resetSelectedMediaItems();

		if (failedDeletions.length > 0) {
			// Silent-failure rule: name every type that failed, with its ids, so a failed document delete
			// is attributable from the log alone.
			console.error("Timeline.deleteMediaItems: bulk delete failed", {
				parentType: props.parentType,
				parentId: props.parentId,
				failedArtifactTypes: failedDeletions.map((deletion) => deletion.artifactType),
				failedIds: failedDeletions.flatMap((deletion) => deletion.items.map((x) => x.id)),
				deletedCount: deletedItems.length,
				errors: results
					.filter((result): result is PromiseRejectedResult => result.status === "rejected")
					.map((result) => result.reason),
				file: "Timeline.vue",
				method: "deleteMediaItems"
			});
			const message = couldNot("delete the selected media");
			toast.error(message, { timeout: calculateTimeout(message) });
		}
	}

	function resetSelectedMediaItems() {
		selectedMediaItems.value = [];
		selecting.value = false;
		selectingChecklistMedia.value = false;
	}

	function pruneSelectedMediaItems() {
		selectedMediaItems.value = selectedMediaItems.value.filter((selectedItem) =>
			props.mediaItems.some((mediaItem) => resultIdentity(mediaItem) === (selectedItem.searchResultId ?? selectedItem.id))
		);
	}

	function toggleItem(item: IndexableDocument) {
		selectionRevision += 1;
		const itemResultIdentity = resultIdentity(item);
		const index = selectedMediaItems.value.findIndex((x) => (x.searchResultId ?? x.id) === itemResultIdentity);

		if (index !== -1) {
			selectedMediaItems.value.splice(index, 1);
		} else {
			selectedMediaItems.value.push(toSelectedMediaItem(item));
		}
	}

	function isItemSelected(item: IndexableDocument) {
		const itemResultIdentity = resultIdentity(item);
		const index = selectedMediaItems.value.findIndex((x) => (x.searchResultId ?? x.id) === itemResultIdentity);
		return index !== -1;
	}

	function getItemSelectionIndex(item: IndexableDocument) {
		const itemResultIdentity = resultIdentity(item);
		const index = selectedMediaItems.value.findIndex((x) => (x.searchResultId ?? x.id) === itemResultIdentity);
		return index + 1;
	}

	function resultIdentity(item: IndexableDocument): string {
		return item.searchResultId ?? item.id;
	}

	async function applyBulkTags(tags: Label[]) {
		try {
			const artifacts = props.mediaItems.map((x) => ({
				id: resultIdentity(x),
				conversationId: x.conversationId
			})) as Artifact[];
			const selectedSnapshots = selectedMediaItems.value.filter((x) => x.type === ArtifactType.Snapshots).map((x) => x.id);
			const selectedRecordings = selectedMediaItems.value.filter((x) => x.type === ArtifactType.Recordings).map((x) => x.id);
			const selectedFiles = selectedMediaItems.value.filter((x) => x.type === ArtifactType.Files).map((x) => x.id);

			if (selectedSnapshots.length > 0) {
				await tagArtifacts(ArtifactType.Snapshots, selectedSnapshots, artifacts, tags, "add");
			}
			if (selectedRecordings.length > 0) {
				await tagArtifacts(ArtifactType.Recordings, selectedRecordings, artifacts, tags, "add");
			}
			if (selectedFiles.length > 0) {
				await tagArtifacts(ArtifactType.Files, selectedFiles, artifacts, tags, "add");
			}

			emit("reload");
		} catch (err) {
			console.error(err);
			console.error(err, {
				tags: {
					parentType: props.parentType,
					parentId: props.parentId,
					parentName: props.parentName
				},
				extra: {
					file: "Timeline.vue",
					method: "applyBulkTags"
				}
			});
		} finally {
			bulkTagDialogVisible.value = false;
			resetSelectedMediaItems();
		}
	}

	function sessionRoute(mediaItem: IndexableDocument): string | undefined {
		return mediaItem.conversationId ? `/conversations/${mediaItem.conversationId}` : undefined;
	}

	async function navigateToSession(mediaItem: IndexableDocument, event: MouseEvent) {
		event.stopPropagation();
		if (!mediaItem.conversationId || !props.isAuthenticated || !me.value) {
			event.preventDefault();
			return;
		}

		if (mediaItem.organizationId && mediaItem.organizationId !== me.value.organization?.id) {
			event.preventDefault();
			await (instance?.proxy as any).$root.$confirm.open(
				"Heads up!",
				"This stuff is in another account. Contact the project owner to get access to the account.",
				false,
				"OK",
				"",
				false,
				true
			);
			return;
		}
		if (shouldLetBrowserHandleClick(event)) return;

		event.preventDefault();
		(instance?.proxy as any)?.$router.push(`/conversations/${mediaItem.conversationId}`);
	}

	// Day view drag-and-drop

	function isDateEditableEntity(item: IndexableDocument): boolean {
		return item.entityType === "snapshot" || item.entityType === "recording";
	}

	function clearDayViewDragState() {
		draggedItem.value = null;
		dragOverDateKey.value = null;
	}

	function getMediaItemMoment(createdAt: string | undefined) {
		if (!createdAt) {
			return null;
		}

		const itemMoment = moment.tz(createdAt, timelineTimezone);
		return itemMoment.isValid() ? itemMoment : null;
	}

	function getMediaItemDateKey(mediaItem: IndexableDocument): string | null {
		const itemMoment = getMediaItemMoment(mediaItem.created);
		return itemMoment ? itemMoment.format("YYYY-MM-DD") : null;
	}

	function isDroppableDateKey(targetDateKey: string): boolean {
		return targetDateKey !== UNDATED_DATE_KEY;
	}

	function getInternalDayViewDragItemId(event: DragEvent): string | null {
		if (!event.dataTransfer) {
			return null;
		}

		const payloadItemId = event.dataTransfer.getData(DAY_VIEW_DRAG_MIME_TYPE);
		if (payloadItemId) {
			return payloadItemId;
		}

		const textPayloadItemId = event.dataTransfer.getData("text/plain");
		return textPayloadItemId || null;
	}

	function resolveDraggedMediaItem(event: DragEvent): IndexableDocument | null {
		const draggedItemId = getInternalDayViewDragItemId(event);
		if (draggedItemId) {
			const mediaItemFromPayload = props.mediaItems.find((mediaItem) => resultIdentity(mediaItem) === draggedItemId);
			if (mediaItemFromPayload) {
				return mediaItemFromPayload;
			}
		}

		return draggedItem.value;
	}

	function canDropToDate(targetDateKey: string): boolean {
		return dayView.value && canEditArtifactDates.value && !selecting.value && isDroppableDateKey(targetDateKey);
	}

	function setLocalArtifactDate(itemResultIdentity: string, newDateTime: string, targetDateKey: string) {
		const localItemIndex = props.mediaItems.findIndex((mediaItem) => resultIdentity(mediaItem) === itemResultIdentity);
		if (localItemIndex === -1) {
			// The server accepted the date change (the caller already showed the success toast) but the
			// dragged row is no longer in the live list — props.mediaItems can be replaced by a reload,
			// a live search update, or a re-search across the await in onDrop. The UI silently keeps the
			// old date until the next reload, so log the mismatch rather than leaving a "the toast said
			// it worked but nothing moved" report to be reconstructed from scratch.
			console.warn("Timeline.setLocalArtifactDate: no live media item matched the updated identity", {
				itemResultIdentity,
				targetDateKey,
				newDateTime,
				loadedMediaItems: props.mediaItems.length
			});
			return;
		}

		const items = [...props.mediaItems];
		const [localItem] = items.splice(localItemIndex, 1);
		const updatedItem = { ...localItem, created: newDateTime };

		const firstItemInTargetDateIndex = items.findIndex((mediaItem) => getMediaItemDateKey(mediaItem) === targetDateKey);
		if (firstItemInTargetDateIndex >= 0) {
			items.splice(firstItemInTargetDateIndex, 0, updatedItem);
		} else {
			items.splice(localItemIndex, 0, updatedItem);
		}

		emit("update:mediaItems", items);
	}

	function onDragStart(event: DragEvent, mediaItem: IndexableDocument) {
		if (!dayView.value || !canEditArtifactDates.value || selecting.value || !isDateEditableEntity(mediaItem)) {
			return;
		}

		draggedItem.value = mediaItem;
		dragOverDateKey.value = null;

		if (event.dataTransfer) {
			event.dataTransfer.effectAllowed = "move";
			event.dataTransfer.setData(DAY_VIEW_DRAG_MIME_TYPE, resultIdentity(mediaItem));
			event.dataTransfer.setData("text/plain", resultIdentity(mediaItem));
		}
	}

	function onDragOver(event: DragEvent, targetDateKey: string) {
		if (!canDropToDate(targetDateKey) || !draggedItem.value) {
			return;
		}

		event.preventDefault();
		if (event.dataTransfer) {
			event.dataTransfer.dropEffect = "move";
		}

		const sourceDateKey = getMediaItemDateKey(draggedItem.value);
		dragOverDateKey.value = sourceDateKey === targetDateKey ? null : targetDateKey;
	}

	function onDragLeave(event: DragEvent) {
		const currentTarget = event.currentTarget as HTMLElement;
		const relatedTarget = event.relatedTarget as Node | null;
		if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
			dragOverDateKey.value = null;
		}
	}

	function onDragEnd() {
		clearDayViewDragState();
	}

	async function onDrop(event: DragEvent, targetDateKey: string) {
		event.preventDefault();

		const item = resolveDraggedMediaItem(event);
		clearDayViewDragState();

		if (!canDropToDate(targetDateKey) || !item) {
			return;
		}

		const sourceMoment = getMediaItemMoment(item.created);
		if (!sourceMoment) {
			const message = "Something went wrong updating the date. Please try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
			return;
		}

		// Determine source dateKey using same timezone logic as day view grouping
		const sourceDateKey = sourceMoment.format("YYYY-MM-DD");

		// No-op if dropped on the same day
		if (sourceDateKey === targetDateKey) {
			return;
		}

		// Preserve local time, change only local date in the same timezone context
		const targetMoment = moment.tz(targetDateKey, "YYYY-MM-DD", timelineTimezone);
		if (!targetMoment.isValid()) {
			const message = "Something went wrong updating the date. Please try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
			return;
		}
		targetMoment
			.hour(sourceMoment.hour())
			.minute(sourceMoment.minute())
			.second(sourceMoment.second())
			.millisecond(sourceMoment.millisecond());
		const newDateTime = targetMoment.toISOString();

		const artifactType = item.entityType === "snapshot" ? "snapshots" : item.entityType === "recording" ? "recordings" : null;

		if (!artifactType) {
			const message = "Date updates are only available for photos and videos.";
			toast.error(message, { timeout: calculateTimeout(message) });
			return;
		}

		try {
			await api.updateArtifactDate(artifactType, resultIdentity(item), newDateTime);
			setLocalArtifactDate(resultIdentity(item), newDateTime, targetDateKey);
			const message = "Date updated successfully.";
			toast.success(message, { timeout: calculateTimeout(message) });
		} catch (err) {
			console.error(err);
			const message = "Something went wrong updating the date. Please try again.";
			toast.error(message, { timeout: calculateTimeout(message) });
		}
	}
</script>

<style scoped lang="scss">
	.content-date-header {
		font-size: 1rem;
		font-weight: 500;
	}

	.result-count {
		font-size: 0.75rem;
	}

	.no-data {
		color: #00000088;
		cursor: default;
		word-break: break-all;
	}

	.pending-badge {
		position: absolute;
		top: 0.25rem;
		left: 0.25rem;
		z-index: 1;
	}

	.day-view-checkbox {
		flex: none;

		:deep(.v-label) {
			font-size: 0.875rem;
		}
	}

	.day-group {
		border: 2px dashed transparent;
		border-radius: 8px;
		transition: border-color 0.2s ease;

		&.drag-over {
			border-color: rgb(var(--v-theme-primary));
			background-color: rgba(var(--v-theme-primary), 0.04);
		}
	}

	.media-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
		gap: 0.25rem;
		margin-top: 4px;
		margin-bottom: 4px;

		@media (max-width: 5120px) {
			grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
		}

		@media (max-width: 2560px) {
			grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
		}

		@media screen and (max-width: 2047px) {
			grid-template-columns: repeat(auto-fill, minmax(128px, 1fr));
		}

		@media #{map.get($display-breakpoints, 'sm-and-down')} {
			grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
		}

		@media #{map.get($display-breakpoints, 'sm-and-down')} and (orientation: landscape) {
			grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
		}
	}

	// LSP-15573: the failed-upload state stands in for the full-width "There's no media here yet" label,
	// which renders outside .media-grid entirely. It is deliberately NOT a .media-grid-item: that class
	// exists for square artwork tiles (aspect-ratio 1/1, a 160px square intrinsic size, one auto-fill
	// column — minmax(80px, 1fr) at sm-and-down), and borrowing it cropped an icon plus two lines of
	// explanatory copy into a small square (Bugbot). It is still a grid CHILD, so it spans every column to
	// occupy the same full-width slot the label it replaces does, and takes its height from its content.
	// Keeping it off .media-grid-item also leaves that class's track-sizing contract untouched — the
	// LSP-17056 guard in tests/unit/mediaGridTrackSizing.spec.ts forbids grid placement on media tiles.
	.failed-upload-placeholder {
		grid-column: 1 / -1;
		background-color: rgb(var(--v-theme-surface));
		color: rgb(var(--v-theme-on-surface));
		// LSP-15573 follow-up: warning, not error — see the icon comment in the template. Theme token
		// rather than a literal yellow so it tracks the palette (and matches the sibling
		// warning surfaces, e.g. SparkGuidesIntroDialog's rgba(var(--v-theme-warning), 0.22) border).
		border: 1px dashed rgba(var(--v-theme-warning), 0.5);
		border-radius: 4px;
		cursor: default;
	}

	.media-grid-item {
		position: relative;
		aspect-ratio: 1/1;
		content-visibility: auto;
		contain-intrinsic-size: 160px 160px;
		cursor: pointer;
		background-color: #000;
		border-radius: 4px;

		.media-tile-actions {
			position: absolute;
			top: 4px;
			right: 4%;
			z-index: 3;
		}

		.selection-bubble {
			min-width: 24px;
			height: 24px;
			padding: 0 2px;
			border-radius: 999px;
			border: 2px solid rgb(var(--v-theme-primary));
			background-color: #fff;
			z-index: 3;
			display: flex;
			align-items: center;
			justify-content: center;
			transition:
				background-color 0.3s ease,
				border-color 0.3s ease,
				color 0.3s ease;

			.text {
				white-space: nowrap;
				line-height: 1;
				font-variant-numeric: tabular-nums;
			}
		}

		.selected {
			background-color: rgb(var(--v-theme-primary));
			border-color: rgb(var(--v-theme-primary));
			color: #fff;
			font-size: 0.725rem;
		}
	}

	.timeline-pagination-sentinel {
		grid-column: 1 / -1;
		height: 1px;
	}

	.media-grid-item video {
		max-width: 100%;
		object-fit: cover;
	}

	.media-grid-item[draggable="true"] {
		cursor: grab;

		&:active {
			cursor: grabbing;
		}
	}

	.session-number {
		background-color: #00000088;
		width: 100%;
		position: absolute;
		bottom: 0;
		left: 0;
		color: #fff;
		font-size: 12px;
		font-weight: 600;
		z-index: 2;
		text-decoration: none;
	}
	// LSP-16659: the document tile's light artwork needs a light session label (the approved design's pill).
	.session-number--document {
		width: auto;
		left: 6px;
		bottom: 6px;
		border-radius: 4px;
		background-color: rgba(255, 255, 255, 0.85);
		color: rgb(var(--v-theme-on-surface));
		font-size: 11px;
	}
	.preview-container {
		position: relative;
		overflow: hidden;
	}

	.media-copy-overlay {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		pointer-events: auto;
		z-index: 1;
		object-fit: cover;

		// Android Chrome drops the first touch on this transparent copy overlay.
		// Only touch-only input should reach the media-grid item's lightbox handler;
		// narrow mouse-capable browsers still need this overlay for image copying.
		@media (pointer: coarse) and (hover: none) and (any-hover: none) {
			pointer-events: none;
		}
	}
</style>
